import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { aepAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search, Loader2, ShieldCheck, ExternalLink, ChevronDown, ChevronUp,
  CheckCircle2, Info, ArrowRight, LogOut, KeyRound, AlertCircle,
  Award, Globe, Mail, Phone, ClipboardCheck, Upload, X,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';

const STATUS_OPTIONS = [
  { value: 'missing',    label: 'Missing',    dot: 'bg-red-500',    pill: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'in_process', label: 'In Process', dot: 'bg-amber-500',  pill: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'complete',   label: 'Complete',   dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'na',         label: 'N/A',        dot: 'bg-slate-400',   pill: 'bg-slate-100 text-slate-500 border-slate-200' },
];

// Contact info shown in the public footer.
const CONTACT = {
  website: 'krenterprisegroup.app',
  email: 'office@krenterprisegroup.com',
  phone: '863-288-0577',
};

// ─────────────────────────────────────────────────────────────
// Public /aep — self-service AEP certification tracker.
// ─────────────────────────────────────────────────────────────
export default function PublicAepPage() {
  const [step, setStep] = useState('search');
  const [selected, setSelected] = useState(null);
  const [session, setSession] = useState(null);

  const handleSignedOut = useCallback(() => {
    setStep('search');
    setSelected(null);
    setSession(null);
  }, []);

  const inSplash = step === 'search' || step === 'npn';

  return (
    <div className={inSplash ? 'min-h-screen bg-white relative overflow-hidden' : 'min-h-screen bg-slate-50'}>
      {inSplash && <SplashDecorations />}

      <div className="relative">
        {inSplash ? (
          <SplashHeader />
        ) : (
          <PublicHeader
            onSignOut={handleSignedOut}
            agentName={session?.display_name}
            photoUrl={session?.photo_url}
            agentRef={session?.agent_ref}
            npn={session?.npn}
            onPhotoUpdated={(newUrl) => setSession((s) => ({ ...s, photo_url: newUrl }))}
          />
        )}

        <main className={inSplash
          ? 'max-w-3xl mx-auto px-4 pt-4 pb-10 md:pb-16'
          : 'max-w-7xl mx-auto px-4 py-6 md:py-8'}>
          {step === 'search' && (
            <AgentSearchStep onSelect={(row) => { setSelected(row); setStep('npn'); }} />
          )}
          {step === 'npn' && selected && (
            <NpnVerifyStep
              selected={selected}
              onBack={handleSignedOut}
              onVerified={(sess, npn) => { setSession({ ...sess, npn }); setStep('dashboard'); }}
            />
          )}
          {step === 'dashboard' && session && (
            <AepDashboardStep session={session} setSession={setSession} />
          )}
        </main>
      </div>

      {inSplash && <SplashFooter />}
      {!inSplash && (
        <footer className="max-w-7xl mx-auto px-4 py-6 text-center text-[11px] text-slate-400">
          KR Agency Hub · AEP 2027 Self-Service Tracker · Your NPN acts as a PIN for this page only.
        </footer>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Splash background decorations (matches the mockup)
// ─────────────────────────────────────────────────────────────
function SplashDecorations() {
  return (
    <>
      {/* Top-left navy curve */}
      <div
        aria-hidden
        className="absolute top-0 left-0 w-[42%] max-w-[520px] h-[280px] pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, #0b2b6b 0%, #14396d 60%, #1e4c95 100%)',
          borderBottomRightRadius: '100% 60%',
        }}
      />
      {/* Bottom-right pale wave */}
      <div
        aria-hidden
        className="absolute bottom-24 right-0 w-[38%] max-w-[420px] h-[200px] rounded-l-full pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent, #e6effc 60%)' }}
      />
      {/* Top-right dot grid */}
      <div
        aria-hidden
        className="absolute top-24 right-6 hidden md:block pointer-events-none opacity-40"
        style={{
          width: 120, height: 120,
          backgroundImage: 'radial-gradient(#94a3b8 1.5px, transparent 1.5px)',
          backgroundSize: '14px 14px',
        }}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Splash header (big KR logo + hub title)
// ─────────────────────────────────────────────────────────────
function SplashHeader() {
  return (
    <div className="relative pt-6 md:pt-8 pb-2 text-center">
      {/* KR Enterprise Group brand block — real logo, same asset as landing */}
      <img
        src="/brand/kr-logo.webp"
        alt="KR Enterprise Group — Health & Life Insurance Services"
        className="mx-auto h-32 md:h-36 lg:h-40 w-auto object-contain"
        width="1548"
        height="454"
        decoding="async"
        fetchPriority="high"
        data-testid="aep-splash-brand-logo"
      />

      {/* Award badge + hub title */}
      <div className="mt-8">
        <div className="inline-flex w-16 h-16 rounded-2xl items-center justify-center shadow-lg"
             style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}>
          <Award size={30} className="text-white" strokeWidth={2.2} />
        </div>
      </div>
      <h1 className="mt-4 font-heading font-bold text-slate-900 text-3xl md:text-4xl">
        KR Agency Hub
      </h1>
      <p className="mt-1 text-lg md:text-xl font-semibold text-[#1d4ed8]">
        AEP 2027 Certification Tracker
      </p>
      <div className="mt-3 flex items-center justify-center gap-3">
        <span className="w-24 h-px bg-slate-200" />
        <ShieldCheck size={14} className="text-[#1d4ed8]" />
        <span className="w-24 h-px bg-slate-200" />
      </div>
      <p className="mt-3 text-sm text-slate-500">
        Track your certification progress for the 2027 AEP season.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Internal (post-verify) header — compact
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// Small helper: extract initials from "First Last" → "FL". Falls
// back to "A" when only one word is available. Kept side-effect-
// free so React can memo it easily.
// ─────────────────────────────────────────────────────────────
function initialsFrom(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'A';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─────────────────────────────────────────────────────────────
// Internal page header (rendered on the dashboard step). Shows the
// agent's real profile photo (falls back to their initials on a
// coloured tile) alongside the "Signed in as agent" label, and lets
// the signed-in agent upload/replace their headshot inline.
//
// Uploads flow through /api/aep/public/photo which:
//   1. Re-verifies (agent_ref, npn) exactly like every other public
//      write on this page — so the trust boundary matches the rest.
//   2. Stores the photo on `agents.photo_url` — the SAME field the
//      Agent Profile inside the Hub reads from — so a public upload
//      instantly surfaces on the internal agent card too.
// ─────────────────────────────────────────────────────────────
function PublicHeader({ onSignOut, agentName, photoUrl, agentRef, npn, onPhotoUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [imgError, setImgError] = useState(false);
  // Dismissable "add a photo" nudge — remembered in localStorage per NPN
  // so the same agent doesn't get pestered forever. Re-opening a
  // browser or a new session is fine; localStorage carries across.
  const [nudgeDismissed, setNudgeDismissed] = useState(true);
  const fileRef = useRef(null);

  // Reset the broken-image flag whenever a fresh URL comes in so a new
  // upload replaces a previously-failing image without a page reload.
  useEffect(() => { setImgError(false); }, [photoUrl]);

  // Hydrate the nudge state after we know which NPN we're rendering.
  useEffect(() => {
    if (!npn) { setNudgeDismissed(true); return; }
    try {
      const key = `aep:photo-nudge-dismissed:${npn}`;
      setNudgeDismissed(localStorage.getItem(key) === '1');
    } catch {
      setNudgeDismissed(false);
    }
  }, [npn]);

  const dismissNudge = (e) => {
    e?.stopPropagation();
    setNudgeDismissed(true);
    try {
      if (npn) localStorage.setItem(`aep:photo-nudge-dismissed:${npn}`, '1');
    } catch { /* private mode → in-memory only */ }
  };

  const canUpload = Boolean(agentRef && npn);
  const busy = uploading || removing;

  const handleFile = async (evt) => {
    const file = evt.target?.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error('Please pick a JPG, PNG or WebP image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be 10 MB or smaller.');
      return;
    }
    setUploading(true);
    try {
      const { data } = await aepAPI.publicUploadPhoto(agentRef, npn, file);
      toast.success('Photo updated');
      onPhotoUpdated?.(data.photo_url || null);
      // Successful upload silences the nudge for good (no need to prod
      // an agent who already added a headshot).
      dismissNudge();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Photo upload failed. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Photo upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async (evt) => {
    // Prevent the click from bubbling to the avatar upload trigger.
    evt.stopPropagation();
    if (!canUpload || busy) return;
    setRemoving(true);
    try {
      await aepAPI.publicDeletePhoto(agentRef, npn);
      toast.success('Photo removed');
      onPhotoUpdated?.(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not remove the photo.';
      toast.error(typeof msg === 'string' ? msg : 'Could not remove the photo.');
    } finally {
      setRemoving(false);
    }
  };

  const showPhoto = photoUrl && !imgError;
  const canRemove = canUpload && showPhoto;
  // The nudge only shows when everything lines up: signed-in agent, no
  // photo yet, and the agent hasn't dismissed it before. Rendered as a
  // small dismissable pill anchored under the avatar so it doesn't push
  // the header layout around.
  const showNudge = canUpload && !showPhoto && !busy && !nudgeDismissed;

  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img
            src="/brand/kr-logo-tagline.webp"
            alt="KR Enterprise Group"
            className="h-10 w-auto object-contain"
            width="1548"
            height="454"
            decoding="async"
            data-testid="aep-internal-header-logo"
          />
          <div className="hidden sm:block h-8 w-px bg-slate-200" />
          <div>
            <p className="text-xs text-slate-500 leading-tight">KR Agency Hub</p>
            <p className="text-xl font-heading font-bold text-[#0b2b6b] leading-tight">
              AEP 2027 Certification Tracker
            </p>
            <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
              Track your certification progress for the 2027 AEP season.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {agentName && (
            <div className="hidden sm:flex items-center gap-2 pr-2 border-r border-slate-200">
              {/* Avatar wrapper is a div (not a button) so the remove
                  "×" button can sit on top without nesting <button>s. */}
              <div className="relative group">
                <button
                  type="button"
                  onClick={() => canUpload && !busy && fileRef.current?.click()}
                  disabled={!canUpload || busy}
                  className={`w-10 h-10 rounded-full overflow-hidden flex items-center justify-center ring-1 ring-slate-200 transition-all ${canUpload ? 'hover:ring-2 hover:ring-[#0b2b6b] cursor-pointer' : 'cursor-default'} ${showPhoto ? 'bg-slate-100' : 'bg-[#0b2b6b] text-white'}`}
                  title={canUpload ? (showPhoto ? 'Change photo' : 'Upload photo') : undefined}
                  data-testid="aep-public-agent-avatar"
                >
                  {showPhoto ? (
                    <img
                      src={photoUrl}
                      alt={agentName}
                      className="w-full h-full object-cover"
                      onError={() => setImgError(true)}
                    />
                  ) : (
                    <span className="text-sm font-semibold" data-testid="aep-public-agent-initials">
                      {initialsFrom(agentName)}
                    </span>
                  )}
                  {/* Hover overlay when upload is possible */}
                  {canUpload && !busy && (
                    <span className="absolute inset-0 bg-black/45 text-white text-[9px] font-semibold uppercase tracking-wider items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex">
                      Change
                    </span>
                  )}
                  {busy && (
                    <span className="absolute inset-0 bg-black/50 text-white flex items-center justify-center">
                      <Loader2 size={14} className="animate-spin" />
                    </span>
                  )}
                </button>
                {/* Remove button — visible on hover only when a photo
                    is actually present. Reverts the agent to their
                    initials tile. */}
                {canRemove && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    disabled={busy}
                    className="absolute -top-1 -right-1 z-10 w-5 h-5 rounded-full bg-rose-600 hover:bg-rose-700 text-white shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove profile photo"
                    title="Remove photo — revert to initials"
                    data-testid="aep-public-agent-remove-btn"
                  >
                    <X size={11} strokeWidth={3} />
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFile}
                  data-testid="aep-public-photo-input"
                />
                {/* Photo onboarding nudge — friendly, self-expression
                    framed (no "so KR knows you", KR already does).
                    Anchored to the avatar so it doesn't push the
                    header layout around. Auto-dismisses on upload
                    and remembers the dismissal per NPN. */}
                {showNudge && (
                  <div
                    className="absolute top-full mt-2 right-0 z-20 w-64 rounded-lg bg-gradient-to-br from-[#0b2b6b] to-[#153f8f] text-white shadow-xl px-3 py-2.5 text-[11.5px] leading-snug animate-in fade-in slide-in-from-top-1 duration-300"
                    role="tooltip"
                    data-testid="aep-photo-nudge"
                  >
                    <button
                      type="button"
                      onClick={dismissNudge}
                      className="absolute top-1 right-1 w-4 h-4 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                      aria-label="Dismiss"
                      data-testid="aep-photo-nudge-dismiss"
                    >
                      <X size={9} strokeWidth={3} />
                    </button>
                    {/* Speech-bubble tail pointing back at the avatar */}
                    <span className="absolute -top-1.5 right-3 w-3 h-3 rotate-45 bg-[#0b2b6b]" aria-hidden />
                    <div className="font-semibold pr-4">Say hi with a headshot</div>
                    <div className="text-white/85 pr-4">
                      Add a friendly photo so your face shows up on your KR
                      profile, referrals, and event pages. Takes 5 seconds.
                    </div>
                    <button
                      type="button"
                      onClick={() => !busy && fileRef.current?.click()}
                      className="mt-1.5 text-white/95 underline hover:text-white text-[11px] font-semibold"
                      data-testid="aep-photo-nudge-cta"
                    >
                      Upload a photo →
                    </button>
                  </div>
                )}
              </div>
              <div className="text-right leading-tight">
                <p className="text-xs font-medium text-slate-800">{agentName}</p>
                <p className="text-[10px] text-slate-400">Signed in as agent</p>
              </div>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onSignOut}
            data-testid="aep-public-switch-agent"
          >
            <LogOut size={14} className="mr-1" /> Switch agent
          </Button>
        </div>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// Splash footer with contact info
// ─────────────────────────────────────────────────────────────
function SplashFooter() {
  return (
    <div className="relative mt-8">
      <p className="max-w-3xl mx-auto px-4 text-center text-sm text-slate-500 mb-4">
        Need assistance? Contact your upline or the KR Enterprise Group office.
      </p>
      <div className="w-full py-4 px-4"
           style={{ background: 'linear-gradient(90deg, #0b2b6b 0%, #14396d 100%)' }}>
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3 text-white text-xs md:text-sm">
          <div className="flex items-center gap-2">
            <Globe size={14} className="opacity-80" />
            <span>{CONTACT.website}</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail size={14} className="opacity-80" />
            <a href={`mailto:${CONTACT.email}`} className="hover:underline">{CONTACT.email}</a>
          </div>
          <div className="flex items-center gap-2">
            <Phone size={14} className="opacity-80" />
            <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, '')}`} className="hover:underline">
              {CONTACT.phone}
            </a>
          </div>
          <div className="italic tracking-wide opacity-90 font-serif">
            We&apos;re here to support you!
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 1 — Agent typeahead search
// ─────────────────────────────────────────────────────────────
function AgentSearchStep({ onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await aepAPI.publicSearch(q.trim());
        setResults(res.data.results || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  return (
    <div className="space-y-4">
      <Card className="shadow-xl border-slate-200 rounded-2xl" data-testid="aep-public-search-card">
        <CardHeader className="pb-2 space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}>
              <Search size={18} className="text-white" />
            </div>
            <div>
              <CardTitle className="text-xl font-heading text-slate-900">
                Find your name
              </CardTitle>
              <p className="text-sm text-slate-500 mt-0.5">
                Start typing your first or last name. You&apos;ll confirm your identity with your NPN in the next step.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1d4ed8]" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => { setQ(e.target.value); setTouched(true); }}
              placeholder="e.g. John Smith"
              className="pl-9 h-12 text-base border-2 border-[#1d4ed8]/60 rounded-lg focus-visible:ring-2 focus-visible:ring-[#1d4ed8]/30"
              data-testid="aep-public-search-input"
            />
            {loading && (
              <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
            )}
          </div>

          {touched && q.trim().length >= 2 && (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 overflow-y-auto" data-testid="aep-public-search-results">
              {results.length === 0 && !loading && (
                <div className="p-4 text-sm text-slate-500 text-center">
                  No matches. Try a different spelling or contact your agency admin.
                </div>
              )}
              {results.map((r) => (
                <button
                  key={r.agent_ref}
                  onClick={() => onSelect(r)}
                  className="w-full text-left px-4 py-3 hover:bg-sky-50 transition flex items-center justify-between gap-2"
                  data-testid={`aep-public-result-${r.agent_ref}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{r.display_name}</p>
                    {r.agency_name && (
                      <p className="text-xs text-slate-500 truncate">{r.agency_name}</p>
                    )}
                  </div>
                  <ArrowRight size={16} className="text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          )}

          {q.trim().length > 0 && q.trim().length < 2 && (
            <p className="text-xs text-slate-400">Type at least 2 characters.</p>
          )}
        </CardContent>
      </Card>

      {/* Important callout */}
      <Card className="border-[#1d4ed8]/20 bg-gradient-to-br from-[#eef2ff] to-white rounded-xl">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-white border border-[#1d4ed8]/30 flex items-center justify-center shrink-0">
            <Info size={16} className="text-[#1d4ed8]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              Important: Updates are completed manually by you.
            </p>
            <p className="mt-1 text-xs text-slate-600">
              Certification statuses must be updated by you as you complete each requirement.
            </p>
            <p className="text-xs text-slate-600">
              Please allow up to 24–48 business hours for updates to appear after your submission.
            </p>
          </div>
          <ClipboardCheck size={40} className="text-[#1d4ed8]/40 shrink-0 hidden sm:block" />
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 2 — NPN verification
// ─────────────────────────────────────────────────────────────
function NpnVerifyStep({ selected, onBack, onVerified }) {
  const [npn, setNpn] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Accept any length ≥1 as long as it's digits — NPNs in the database
  // vary in length. Backend performs the actual match against the on-file NPN.
  const canSubmit = /^\d+$/.test(npn) && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      const res = await aepAPI.publicSession(selected.agent_ref, npn);
      onVerified(res.data, npn);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a minute before trying again.');
      } else {
        setError('Could not verify. Please check your NPN and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="shadow-lg border-slate-200" data-testid="aep-public-npn-card">
      <CardHeader className="pb-3 space-y-1">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <KeyRound size={12} /> Step 2 of 2
        </div>
        <CardTitle className="text-2xl font-heading text-slate-900">
          Confirm your identity
        </CardTitle>
        <p className="text-sm text-slate-500">
          Signing in as <span className="font-semibold text-slate-900">{selected.display_name}</span>
          {selected.agency_name && <span className="text-slate-400"> · {selected.agency_name}</span>}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="npn-input" className="block text-sm font-medium text-slate-700 mb-1">
              Your NPN
            </label>
            <Input
              id="npn-input"
              autoFocus
              inputMode="numeric"
              pattern="\d*"
              value={npn}
              onChange={(e) => { setNpn(e.target.value.replace(/[^0-9]/g, '')); setError(''); }}
              placeholder="e.g. 12345678"
              className="h-12 text-base tracking-wider tabular-nums"
              data-testid="aep-public-npn-input"
            />
            <p className="text-xs text-slate-400 mt-1">
              Your National Producer Number. This acts as a PIN for this page only.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="aep-public-npn-error">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onBack} disabled={submitting}>
              Back
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 bg-sky-600 hover:bg-sky-700"
              data-testid="aep-public-npn-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Continue
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 3 — Certifications dashboard (public view — same shape as
// the internal AepCertificationsTab, but using publicUpdateCert).
// ─────────────────────────────────────────────────────────────
function AepDashboardStep({ session, setSession }) {
  const [savingCarrierId, setSavingCarrierId] = useState(null);
  const [savedRecently, setSavedRecently] = useState({});
  const [expandedInstructions, setExpandedInstructions] = useState({});

  const rows = session.carriers || [];

  const summary = useMemo(() => {
    const total = rows.length;
    const complete = rows.filter((r) => r.status === 'complete').length;
    const inProcess = rows.filter((r) => r.status === 'in_process').length;
    const missing = rows.filter((r) => r.status === 'missing').length;
    const na = rows.filter((r) => r.status === 'na').length;
    // Exclude N/A from the denominator. If everything is N/A, show 100%.
    const activeTotal = total - na;
    const pct = activeTotal === 0 ? 100 : Math.round((complete / activeTotal) * 100);
    return { total, complete, inProcess, missing, na, activeTotal, pct };
  }, [rows]);

  const persistRow = async (carrier_id, status, notes) => {
    setSavingCarrierId(carrier_id);
    try {
      await aepAPI.publicUpdateCert(carrier_id, session.agent_ref, session.npn, status, notes);
      setSession((s) => ({
        ...s,
        carriers: s.carriers.map((c) => c.carrier_id === carrier_id ? { ...c, status, notes: notes || '', updated_at: new Date().toISOString() } : c),
      }));
      setSavedRecently((s) => ({ ...s, [carrier_id]: Date.now() }));
      setTimeout(() => {
        setSavedRecently((s) => {
          const next = { ...s };
          delete next[carrier_id];
          return next;
        });
      }, 2500);
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error('Too many changes. Please wait a moment.');
      } else if (e.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
      } else {
        toast.error('Save failed');
      }
    } finally {
      setSavingCarrierId(null);
    }
  };

  const onStatusChange = (carrier_id, newStatus) => {
    const row = rows.find((r) => r.carrier_id === carrier_id);
    persistRow(carrier_id, newStatus, row?.notes || '');
  };
  const onNotesBlur = (carrier_id, newNotes) => {
    const row = rows.find((r) => r.carrier_id === carrier_id);
    if ((row?.notes || '') === (newNotes || '')) return;
    persistRow(carrier_id, row.status || 'missing', newNotes);
  };

  return (
    <div className="space-y-4" data-testid="aep-public-dashboard">
      {/* Welcome + AEP 2027 PREP badge */}
      <Card className="border-l-4 border-l-sky-500 bg-gradient-to-r from-sky-50 to-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
              <ShieldCheck size={24} className="text-sky-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-heading font-semibold text-slate-900">AEP 2027 PREP</p>
                <Badge className="bg-sky-100 text-sky-700 text-xs">
                  {session.display_name}
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Mark each carrier as you finish certification. Changes save automatically.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress + Summary */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="text-lg font-heading">Certification Progress</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                {summary.complete} of {summary.activeTotal} active carrier{summary.activeTotal === 1 ? '' : 's'} complete
                {summary.na > 0 && (
                  <span className="text-slate-400"> · {summary.na} N/A</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="aep-public-summary-complete">
                {summary.complete} Complete
              </Badge>
              <Badge className="bg-amber-50 text-amber-700 border border-amber-200" data-testid="aep-public-summary-in-process">
                {summary.inProcess} In Process
              </Badge>
              <Badge className="bg-red-50 text-red-700 border border-red-200" data-testid="aep-public-summary-missing">
                {summary.missing} Missing
              </Badge>
              {summary.na > 0 && (
                <Badge className="bg-slate-100 text-slate-500 border border-slate-200" data-testid="aep-public-summary-na">
                  {summary.na} N/A
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500"
                style={{ width: `${summary.pct}%` }}
                data-testid="aep-public-progress-bar"
              />
            </div>
            <span className="text-sm font-heading font-semibold text-slate-900 tabular-nums w-12 text-right">
              {summary.pct}%
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Carriers — Phase 27.30.25: 2 columns on desktop so agents can
          see the full carrier list at a glance without scrolling. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {rows.map((row) => {
          const statusMeta = STATUS_OPTIONS.find((s) => s.value === row.status) || STATUS_OPTIONS[0];
          const isSaving = savingCarrierId === row.carrier_id;
          const justSaved = !!savedRecently[row.carrier_id];
          const instrOpen = !!expandedInstructions[row.carrier_id];
          const hasInstr = !!(row.instructions && row.instructions.trim());
          return (
            <Card key={row.carrier_id} data-testid={`aep-public-row-${row.carrier_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <CarrierLogo
                    row={row}
                    onLogoUploaded={(carrierId, newUrl) => {
                      setSession((s) => ({
                        ...s,
                        carriers: s.carriers.map((c) =>
                          c.carrier_id === carrierId ? { ...c, logo_url: newUrl } : c
                        ),
                      }));
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`w-2.5 h-2.5 rounded-full ${statusMeta.dot} shrink-0`} />
                      <span className="font-heading font-semibold text-slate-900">
                        {row.carrier_name}
                      </span>
                      {row.portal_url && (
                        <a
                          href={row.portal_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 hover:underline"
                        >
                          Portal <ExternalLink size={12} />
                        </a>
                      )}
                      {justSaved && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`aep-public-saved-${row.carrier_id}`}>
                          <CheckCircle2 size={12} /> Saved
                        </span>
                      )}
                      {isSaving && (
                        <Loader2 size={12} className="animate-spin text-slate-400" />
                      )}
                    </div>

                    {hasInstr && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setExpandedInstructions((s) => ({ ...s, [row.carrier_id]: !s[row.carrier_id] }))}
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800"
                        >
                          <Info size={12} />
                          {instrOpen ? 'Hide' : 'Show'} instructions
                          {instrOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                        {instrOpen && (
                          <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 whitespace-pre-wrap">
                            {row.instructions}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-3">
                      <Textarea
                        defaultValue={row.notes || ''}
                        onBlur={(e) => onNotesBlur(row.carrier_id, e.target.value)}
                        placeholder="Notes (optional)"
                        className="text-sm min-h-[52px] resize-y"
                        data-testid={`aep-public-notes-${row.carrier_id}`}
                      />
                    </div>
                  </div>

                  <div className="md:w-48 shrink-0">
                    <Select
                      value={row.status}
                      onValueChange={(v) => onStatusChange(row.carrier_id, v)}
                    >
                      <SelectTrigger
                        className={`w-full border ${statusMeta.pill}`}
                        data-testid={`aep-public-status-${row.carrier_id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span className="inline-flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${opt.dot}`} />
                              {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {row.updated_at && (
                      <p className="text-[10px] text-slate-400 mt-1 text-right">
                        Updated {new Date(row.updated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Carrier logo — shows the uploaded image if present, otherwise a
// tasteful placeholder tile with the carrier's initials.
// ─────────────────────────────────────────────────────────────
function CarrierLogo({ row, onLogoUploaded }) {
  const [failed, setFailed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();
  const canUpload = !!user && isKRRole(user);

  const url = row.logo_url && !failed
    ? (row.logo_url.startsWith('http')
        ? row.logo_url
        : `${process.env.REACT_APP_BACKEND_URL}${row.logo_url}`)
    : null;
  const initials = (row.carrier_name || '')
    .split(/[\s/-]+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]).join('').toUpperCase() || '?';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canUpload) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be ≤ 2 MB');
      return;
    }
    setUploading(true);
    try {
      const res = await aepAPI.adminUploadCarrierLogo(row.carrier_id, file);
      toast.success(`Logo uploaded for ${row.carrier_name}`);
      if (typeof onLogoUploaded === 'function') {
        onLogoUploaded(row.carrier_id, res.data?.logo_url || null);
      }
      setFailed(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Phase 27.30.29 — When no logo is set and the viewer is a KR admin,
  // surface an inline upload target so the catalog can be completed
  // without leaving the /aep grid.
  const showAdminUpload = !url && canUpload;

  return (
    <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 p-2 md:p-2.5 relative"
         data-testid={`aep-public-logo-${row.carrier_id}`}>
      {url ? (
        <img
          src={url}
          alt={row.carrier_name}
          className="w-full h-full object-contain"
          onError={() => setFailed(true)}
        />
      ) : showAdminUpload ? (
        <button
          type="button"
          onClick={() => !uploading && fileRef.current?.click()}
          disabled={uploading}
          className="absolute inset-0 rounded-lg flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:text-[#1d4ed8] hover:bg-blue-50 transition-colors"
          data-testid={`aep-logo-admin-upload-${row.carrier_id}`}
          title="Click to upload a logo (admin only)"
        >
          {uploading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              <Upload size={14} strokeWidth={2.2} />
              <span className="text-[9px] font-semibold uppercase tracking-wider">Add logo</span>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={handleFile}
            className="hidden"
          />
        </button>
      ) : (
        <span className="text-slate-500 font-heading font-semibold text-lg" data-testid={`aep-public-logo-fallback-${row.carrier_id}`}>{initials}</span>
      )}
    </div>
  );
}
